import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import {
  AssignmentStatus,
  SearchValidity,
  SearchValidityKey,
  TruckJourneyAssignmentLoad,
  TruckJourneyLoadSearchCriteria
} from '../../../models/truck-journey.model';
import * as moment from 'moment/moment';
import { DataFiltersComponent, HELPER_POSITION } from '../../../../../shared/data-filters/data-filters.component';
import { ErrorHandlerService } from '../../../../../shared/services/error-handler.service';
import { ActivatedRoute } from '@angular/router';
import { finalize, map } from 'rxjs/operators';
import { LoaderService } from '../../../../../shared/services/loader.service';
import { Option } from '../../../../../shared/data-filters/data-filter-select/data-filter-select.component';
import { TruckJourneyService } from '../../../services/truck-journey.service';
import { Moment } from 'moment';

type SelectableTruckJourneyAssignmentLoad = TruckJourneyAssignmentLoad & { checked: boolean }

@Component({
  selector: 'app-truck-journey-load-assignment',
  styleUrls: ['./truck-journey-load-assignment.component.scss'],
  templateUrl: './truck-journey-load-assignment.component.html'
})
export class TruckJourneyLoadAssignmentComponent implements OnChanges, OnInit, OnDestroy {

  @ViewChild('dataFilters', {static: true}) private filtersComponent: DataFiltersComponent;
  @Input() selectedLoads: TruckJourneyAssignmentLoad[];

  @Output() selectedLoadsChange = new EventEmitter<TruckJourneyAssignmentLoad[]>();
  @Output() onCancel = new EventEmitter<void>();



  public selectableLoads: SelectableTruckJourneyAssignmentLoad[];
  public isLoading: Observable<boolean>;
  private openSubscription: Subscription;
  private filtersBase: TruckJourneyLoadSearchCriteria = {
    carrierId: undefined,
    loadNumber: '',
    deliveryNumber: '',
    vehicle: '',
    appointmentPlannedFrom: moment().startOf('day'),
    appointmentPlannedTo: moment().startOf('day').add(7, 'days'),
    assignmentStatus: AssignmentStatus.UNASSIGNED,
    pickLocationGlids: [],
    dropLocationGlids: []
  };
  public filters: TruckJourneyLoadSearchCriteria = {...this.filtersBase};
  public yesterday: Moment = moment().startOf('day').add(-1, 'days');
  public numberOfSelectedLoads = 0;
  public selectionHasAlreadyAssignedLoad = false;
  public allLoadschecked = false;
  public assignmentStatusOptions: Option[] = [
    {
      value: AssignmentStatus.UNASSIGNED,
      labelKey: `truck-journeys.load-assignment.filter.assignment-status.${AssignmentStatus.UNASSIGNED}`
    },
    {
      value: AssignmentStatus.ASSIGNED,
      labelKey: `truck-journeys.load-assignment.filter.assignment-status.${AssignmentStatus.ASSIGNED}`
    },
    {
      value: AssignmentStatus.ALL,
      labelKey: `truck-journeys.load-assignment.filter.assignment-status.${AssignmentStatus.ALL}`
    }
  ];
  public searchValidity: SearchValidity;
  public readonly HelperPosition = HELPER_POSITION;
  private searchValidityMap = new Map<SearchValidityKey, SearchValidity>();


  @Input()
  set excludedJourneyId(id: string) {
    this.filtersBase.excludedJourneyId = id;
    this.filters.excludedJourneyId = id;
  }


  constructor(
    private truckJourneyService: TruckJourneyService,
    private errorHandler: ErrorHandlerService,
    private route: ActivatedRoute,
    private loader: LoaderService,
    private cdRef: ChangeDetectorRef
  ) {
    this.isLoading = this.loader.getObservable().pipe(map(loadParam => loadParam.loading));
    this.searchValidityMap.set(SearchValidityKey.IS_VALID, {isValid: true, color: null, messageKey: undefined});
    this.searchValidity = this.searchValidityMap.get(SearchValidityKey.IS_VALID);
    this.route.queryParams.subscribe(({carrierId}): void => {
      this.filtersBase.carrierId = carrierId;
      this.resetFilters();
    });
  }

  public ngOnInit(): void {
    if (this.filtersComponent) {
      this.filtersComponent.getResetObservable().subscribe(() => this.resetFilters());
      this.filtersComponent.getApplyObservable().subscribe(() => this.searchLoads());
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedLoads) {
      if (this.selectedLoads) {
        if (this.selectableLoads) {
          const newSelectedLoads: SelectableTruckJourneyAssignmentLoad[] = [];
          newSelectedLoads.push(...this.selectedLoads
              .filter(load => this.selectableLoads.every(alreadyViewedLoad => load.loadId !== alreadyViewedLoad.loadId))
              .map(load => ({...load, checked: true})));
          this.selectableLoads = [...newSelectedLoads, ...this.selectableLoads]
        } else {
          this.selectableLoads = this.selectedLoads.map(load =>
              ({...load, checked: true}));
        }
      } else {
        if (this.selectableLoads) {
          this.selectableLoads.forEach(selectableLoad => selectableLoad.checked = false);
        } else {
          this.selectableLoads = [];
        }
        this.selectionHasAlreadyAssignedLoad = false;
      }
    }
  }

  public reset() {
    this.resetFilters()
  }

  public searchLoads(): void {
    this.loader.startLoad();
    if (this.openSubscription) {
      this.openSubscription.unsubscribe();
    }
    this.openSubscription = this.truckJourneyService.searchLoads(this.filters)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(updatedLoads => {
        this.selectableLoads = this.selectableLoads.filter(load => load.checked);
        this.selectableLoads.push(...updatedLoads
          .filter(load => !this.selectedLoads || this.selectedLoads.every(alreadyViewedLoad => load.loadId !== alreadyViewedLoad.loadId))
          .map(load => ({...load, checked: false}))
        );
      }, error => {
        this.errorHandler.handleHttpError(error, 'truck-journeys.list.search-error');
      });
  }

  public checkSearchValidity(fieldNameChanged: string = undefined): void {
    if (moment(this.filters.appointmentPlannedFrom).add(8, 'days').isBefore(this.filters.appointmentPlannedTo)) {
      if (fieldNameChanged === 'appointmentPlannedTo') {
        this.filters.appointmentPlannedFrom = moment(this.filters.appointmentPlannedTo).subtract(8, 'days');
      } else if (fieldNameChanged === 'appointmentPlannedFrom') {
        this.filters.appointmentPlannedTo = moment(this.filters.appointmentPlannedFrom).add(8, 'days');
      }
      this.cdRef.detectChanges();
    } else {
      this.searchValidity = this.searchValidityMap.get(SearchValidityKey.IS_VALID);
    }
  }

  public resetFilters(): void {
    this.filters = {...this.filtersBase};
  }

  public selectOrUnselectAllLoads(): void {
    for (const selectableLoad of this.selectableLoads) {
      selectableLoad.checked = this.allLoadschecked;
    }
    this.numberOfSelectedLoads = this.selectableLoads.filter(load => load.checked)?.length || 0;
    this.updateSelectionHasAlreadyAssignedLoad();
  }

  public recalculateSelectedResults(): void {
    if (this.selectableLoads) {
      this.updateSelectionHasAlreadyAssignedLoad();
      this.selectedLoads = this.selectableLoads.filter(load => load.checked);
      this.numberOfSelectedLoads = this.selectableLoads.filter(load => load.checked).length;
    } else {
      this.selectedLoads = [];
      this.selectionHasAlreadyAssignedLoad = false;
      this.numberOfSelectedLoads = 0;
    }
    this.selectedLoadsChange.emit(this.selectedLoads);
  }

  public reinitializeSelectableLoads(): void {
    this.reset();
    this.selectableLoads = [];
    this.numberOfSelectedLoads = 0;
  }

  public ngOnDestroy(): void {
    this.openSubscription?.unsubscribe();
  }

  private updateSelectionHasAlreadyAssignedLoad(): void {
    this.selectionHasAlreadyAssignedLoad = this.selectableLoads
      .find(load => load.checked && load.status === AssignmentStatus.ASSIGNED) !== undefined;
  }
}
